import React, { useContext, useState, useEffect, useRef } from 'react';

import { MC_PLANS } from '../../../constants/pricing/plans';
import PricingContext from '../../../context/pricing';
import { setVolume, setContacts } from '../../../actions/pricing';
import { dataLayerPush } from '../../../utils';
import PlanBox from '../PlanBox';
import withMC from '../PlanBox/withMC';
import PlanSlider from '../PlanSlider';
import PlanComparisonLink from '../PlanComparisonLink';
import { StyledPlanGrid } from '../PlanBox/styles';
import MCFreeIcon from '../../SVG/MCFreeIcon';

import BasicHandle from './BasicHandle';
import withSelections from '../withSelections';

const PlanBoxWithMC = withMC(PlanBox);
const maxBasic = Object.values(MC_PLANS.basic.packages).pop();
const maxAdvanced = Object.values(MC_PLANS.advanced.packages).pop();

const MarketingPlans = ({ mcPackage }) => {
  const { state, dispatch, sgPricingVersion } = useContext(PricingContext);
  const isHidden = state.activeTab !== 'mc';

  const [prevPkg, setPrevPkg] = useState(mcPackage);
  const firstUpdate = useRef(true);
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !window.dataLayer ||
      firstUpdate.current
    ) {
      firstUpdate.current = false;
      return;
    }

    const eventAction = mcPackage ? 'add_to_cart' : 'remove_from_cart';
    const packageName = mcPackage.name || prevPkg.name;
    const data = {
      event: 'pricingFunnel',
      eventAction,
      pricingVersion: sgPricingVersion,
      tab: 'mc',
      packageName,
    };

    // Push event to GA
    dataLayerPush(data);
    setPrevPkg(mcPackage);
  }, [mcPackage]);

  const [shouldPop, setShouldPop] = useState({
    basicContacts: false,
    basicVolume: false,
  });

  const handleBasicContactsChange = (contacts) => {
    setContacts('basic', contacts, dispatch);
    const maxContacts = maxBasic.contacts;
    if (contacts === maxContacts) {
      setShouldPop({ ...shouldPop, basicContacts: true, basicVolume: false });
    } else {
      setShouldPop({ ...shouldPop, basicContacts: false });
    }
  };

  const handlBasicVolumeChange = (volume) => {
    setVolume('mc', 'basic', volume, dispatch);
    const contacts = state.contacts.basic;
    const maxContacts = maxBasic.contacts;
    const maxVolume = maxBasic.volume;
    if (volume >= maxVolume && contacts >= maxContacts) {
      setShouldPop({ ...shouldPop, basicContacts: false, basicVolume: true });
    } else if (volume >= maxVolume && contacts < maxContacts) {
      setShouldPop({ ...shouldPop, basicVolume: true });
    } else {
      setShouldPop({ ...shouldPop, basicVolume: false });
    }
  };

  return (
    <div
      id="mc-panel"
      role="tabpanel"
      data-testid="mc-panel"
      aria-labelledby="mc-tab"
      aria-hidden={isHidden}
      className={`${
        isHidden ? 'd-none ' : ''
      }p-relative bg-offset-short-gray-900-bottom bg-offset-hidden-tablet`}
    >
      <div className="row row--xlarge p-top-10 p-bottom-10">
        <div className="m-bottom-5 text-center">
          <h2>Marketing Campaigns Plans</h2>
        </div>
        <StyledPlanGrid className="has-three-columns">
          <PlanBoxWithMC plan={MC_PLANS.free}>
            <MCFreeIcon className="mc-free-icon m-top-5 m-bottom-down-2" />
          </PlanBoxWithMC>

          <PlanBoxWithMC plan={MC_PLANS.basic}>
            <PlanSlider
              max={maxBasic.contacts}
              maxLabel={`${maxBasic.contacts.toLocaleString()}+`}
              unit="contacts"
              value={state.contacts.basic}
              handle={({ ref, ...props }) => (
                <BasicHandle
                  forwardedRef={ref}
                  shouldPop={shouldPop.basicContacts}
                  {...props}
                />
              )}
              onChange={handleBasicContactsChange}
              testid="slider-contacts-basic"
            />
            <PlanSlider
              max={maxBasic.volume}
              maxLabel={`${maxBasic.volume.toLocaleString()}+`}
              unit="emails/mo"
              value={state.volume.mc.basic}
              handle={({ ref, ...props }) => (
                <BasicHandle
                  forwardedRef={ref}
                  shouldPop={shouldPop.basicVolume}
                  {...props}
                />
              )}
              onChange={handlBasicVolumeChange}
              testid="slider-volume-basic"
            />
          </PlanBoxWithMC>

          <PlanBoxWithMC plan={MC_PLANS.advanced}>
            <span className="tag tag--pink-dark">Popular</span>
            <PlanSlider
              max={maxAdvanced.contacts}
              maxLabel={`${maxAdvanced.contacts.toLocaleString()}+`}
              unit="contacts"
              value={state.contacts.advanced}
              onChange={(val) => setContacts('advanced', val, dispatch)}
              testid="slider-contacts-advanced"
            />
            <PlanSlider
              max={maxAdvanced.volume}
              maxLabel="1 million+"
              unit="emails/mo"
              value={state.volume.mc.advanced}
              onChange={(val) => setVolume('mc', 'advanced', val, dispatch)}
              testid="slider-volume-advanced"
            />
          </PlanBoxWithMC>
        </StyledPlanGrid>
        <PlanComparisonLink />
      </div>
    </div>
  );
};

export default withSelections(MarketingPlans);
